import { useEffect } from 'react';
import {
  XMarkIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { useAppStore } from '@/stores/appStore';

const STYLES = {
  error: {
    container: 'bg-red-50 border-red-200 text-red-700',
    Icon: ExclamationCircleIcon,
    iconColor: 'text-red-500',
  },
  success: {
    container: 'bg-green-50 border-green-200 text-green-700',
    Icon: CheckCircleIcon,
    iconColor: 'text-green-500',
  },
  info: {
    container: 'bg-blue-50 border-blue-200 text-blue-700',
    Icon: InformationCircleIcon,
    iconColor: 'text-blue-500',
  },
} as const;

/**
 * Global notification banner rendered in the header, centered between the
 * navigation and the action buttons. Errors persist until dismissed;
 * success/info auto-dismiss after a few seconds.
 */
export function GlobalNotification() {
  const notification = useAppStore((s) => s.notification);
  const dismissNotification = useAppStore((s) => s.dismissNotification);

  // Auto-dismiss success/info; errors stay until the user closes them.
  useEffect(() => {
    if (!notification || notification.type === 'error') return;
    const timer = setTimeout(dismissNotification, 4000);
    return () => clearTimeout(timer);
  }, [notification?.id]);

  if (!notification) return null;

  const style = STYLES[notification.type];
  const { Icon } = style;

  return (
    <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 z-40 max-w-md w-full px-4 pointer-events-none">
      <div
        role="status"
        className={`pointer-events-auto flex items-center gap-2 px-3 py-1.5 rounded-md border shadow-sm text-sm ${style.container}`}
      >
        <Icon className={`w-4 h-4 flex-shrink-0 ${style.iconColor}`} />
        <span className="min-w-0 flex-1 truncate" title={notification.message}>
          {notification.message}
        </span>
        <button
          onClick={dismissNotification}
          className="flex-shrink-0 p-0.5 rounded hover:bg-black/5 transition-colors"
          title="Dismiss"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
